import React, { FC } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';

import { navigateBack, isPhoneNative, isWeb } from '@helpers';
import i18n from 'i18n-js';

import Svg from '@styles/icons/svg';

import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { FontStyles, font as initialFont } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { SimpleOnPress } from '@interfaces/base';
import { ColorFunction } from '@styles/colors/types';
import { IconConfig } from '@components/operatorPanel/interfaces';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

interface BackButtonProps extends Partial<SimpleOnPress> {
  scale?: (n: number) => number;
  font?: FontStyles;
  fontType?: keyof FontStyles;
  text?: string;
  backArrow?: boolean;
  color?: keyof FontStyles;
  style?: StyleProp<ViewStyle>;
  withoutText?: boolean;
  C: ColorFunction;
  testID?: string;
  textTestID?: string;
  iconConfig?: Partial<IconConfig>;
}

const BackComponent: FC<BackButtonProps> = ({
  scale = initialScale,
  font = initialFont,
  fontType = 'Body',
  C,
  text,
  onPress,
  backArrow = true,
  color,
  style,
  withoutText,
  testID,
  textTestID,
  iconConfig,
}) => {
  const iconWidth = scale(iconConfig?.width ?? (isPhoneNative ? 18 : 11));
  const iconHeight = scale(iconConfig?.height ?? (isPhoneNative ? 18 : 20));
  const ghostIconWidth = scale(iconConfig?.width ?? 11);
  const ghostIconHeight = scale(iconConfig?.height ?? 20);
  return (
    <View style={style ?? { paddingHorizontal: scale(12) }}>
      <TouchableElement testID={testID} onPress={() => (onPress ? onPress() : navigateBack())}>
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              {
                flexDirection: 'row',
                alignItems: 'center',
                position: 'relative',
              },
              boxStyle,
            ]}
          >
            <View
              style={{
                backgroundColor: C(pressHoverColor('Button/Light/Only Text')),
                flexDirection: 'row',
                paddingHorizontal: scale(12),
                paddingVertical: scale(7),
                position: 'absolute',
                top: scale(-7),
                left: scale(-12),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                alignContent: 'center',
                alignItems: 'center',
                gap: scale(6),
              }}
            >
              {backArrow && (
                <Svg
                  icon="chevronLeft"
                  color={color || 'Link / Light / Primary'}
                  {...iconConfig}
                  width={iconWidth}
                  height={iconHeight}
                />
              )}
              {!withoutText && (
                <Text
                  testID={textTestID}
                  numberOfLines={1}
                  style={[
                    {
                      color: C(pressHoverColor(color || 'Link / Light / Primary')),
                    },
                    font[fontType],
                    isWeb && {
                      whiteSpace: 'nowrap',
                    },
                  ]}
                >
                  {i18n.t(text ?? 'Back')}
                </Text>
              )}
            </View>
            <View
              style={{
                opacity: 0,
                flexDirection: 'row',
              }}
            >
              {backArrow && (
                <Svg
                  icon="chevronLeft"
                  color={color || 'Link / Light / Primary'}
                  {...iconConfig}
                  width={ghostIconWidth}
                  height={ghostIconHeight}
                />
              )}
              {!withoutText && (
                <Text
                  numberOfLines={1}
                  style={[
                    {
                      marginLeft: scale(7),
                    },
                    font[fontType],
                    isWeb && {
                      whiteSpace: 'nowrap',
                    },
                  ]}
                >
                  {i18n.t(text ?? 'Back')}
                </Text>
              )}
            </View>
          </View>
        )}
      </TouchableElement>
    </View>
  );
};

export default ColorWrapper(BackComponent);
